import React from 'react';
import AdvanceSubscriptionCard from './AdvanceSubscriptionCard';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CalendarDay } from 'further-ui/utils';
import EqualisationsTableHeader from 'components/EqualisationElements/EqualisationsTableHeader';
import { Button, Grid, Typography } from '@mui/material';
import GridContainer from 'components/GridContainer';
import ButtonGroup from 'components/ButtonGroup';
import Spacing from 'components/Spacing';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import { useFormContext } from 'react-hook-form';
import { PartnerTransfersFormValues } from '../../hooks/usePartnerTransfersForm';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import { AllocationRule } from 'further-types/lp/allocations';

interface AdvanceSubscription {
  actionId: string;
  entryId: string;
  name: string;
  ledgerDate: CalendarDay;
  actionDueDate?: CalendarDay;
  totalAmountLcy: number;
  allocationRule: AllocationRule;
  originalInvestorBreakdown: Array<{
    commitmentId: string;
    investorName: string;
    totalCommitmentLcy: number;
    calledFundsLcy: number;
    allocationPercentage: number;
  }>;
  newInvestorBreakdown: Array<{
    commitmentId: string;
    investorName: string;
    totalCommitmentLcy: number;
    calledFundsLcy: number;
    allocationPercentage: number;
    excludeFromAllocation: boolean;
    callOverpaidOrUnderpaid: number;
  }>;
  numberOfEntries: number;
}

interface AdvanceSubscriptionsContentProps {
  partnerTransferId: string;
  transferDate: CalendarDay;
  advanceSubscriptions: Array<AdvanceSubscription>;
  baseCurrency: CurrencyCode;
  onNext: () => void;
  onPrevious: () => void;
}

const AdvanceSubscriptionsContent: React.FC<
  AdvanceSubscriptionsContentProps
> = ({
  partnerTransferId,
  transferDate,
  advanceSubscriptions,
  baseCurrency,
  onNext,
  onPrevious,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const { getValues } = useFormContext<PartnerTransfersFormValues>();

  const updatePartnerTransfer = api.partnerTransfers.update.useMutation();

  const handleNext = () => {
    const formValues = getValues();
    const input = {
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
      transferDate: formValues.transferDate,
      transferorCommitmentId: formValues.trasferrorCommitmentId,
      currentSection: PartnerTransferSection.AdvanceSubscriptions,
      transferees: formValues.transferees.map((transferee) => ({
        investorId: transferee.investorId,
        amountLcy: transferee.amountLcy,
        percentage: transferee.percentage,
      })),
      saveActionAdvanceSubscriptions: true,
    };
    updatePartnerTransfer.mutate(input, {
      onSuccess: () => {
        onNext();
      },
    });
  };

  const handlePrevious = () => {
    onPrevious();
  };

  const hasAdvanceSubscriptions =
    advanceSubscriptions && advanceSubscriptions.length > 0;

  return (
    <GridContainer>
      <Grid size={{ xs: 12, lg: 12 }}>
        {!hasAdvanceSubscriptions ? (
          <Spacing size="lg">
            <Typography variant="body1">
              No advance subscriptions found for this partner transfer.
            </Typography>
          </Spacing>
        ) : (
          <>
            <EqualisationsTableHeader
              headers={['Name', 'Date', 'Amount']}
              gridTemplateColumns="60% 20% 20%"
            />
            {advanceSubscriptions.map((advanceSubscription, index) => (
              <AdvanceSubscriptionCard
                key={advanceSubscription.actionId}
                advanceSubscription={advanceSubscription}
                index={index}
                isLastElement={index === advanceSubscriptions.length - 1}
                baseCurrency={baseCurrency}
                transferDate={transferDate}
              />
            ))}
          </>
        )}
      </Grid>
      <Grid size={{ xs: 12, lg: 12 }}>
        <ButtonGroup>
          <Button
            variant="outlined"
            onClick={handlePrevious}
            disabled={updatePartnerTransfer.isPending}
          >
            Previous step
          </Button>

          <Button
            variant="contained"
            onClick={handleNext}
            disabled={updatePartnerTransfer.isPending}
            loading={updatePartnerTransfer.isPending}
          >
            Save and continue
          </Button>
        </ButtonGroup>
      </Grid>
    </GridContainer>
  );
};

export default AdvanceSubscriptionsContent;
